import { useState, useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { getDealActivities } from '../../api/queries_v2';

export interface DealActivity {
  id: string;
  verb: string;
  time: string;
  description: string;
  actor_data?: {
    id: string;
    email: string;
    full_name: string;
  };
  object_data?: {
    object_id: string;
    object_type: string;
  };
  additional_message?: string;
}

interface UseDealActivitiesOptions {
  dealId: string | undefined;
  limit?: number;
  pollInterval?: number;
}

interface UseDealActivitiesReturn {
  activities: DealActivity[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  totalLoaded: number;
}

const DEFAULT_LIMIT = 25;
const DEFAULT_POLL_INTERVAL = 30000;

export const useDealActivities = ({
  dealId,
  limit = DEFAULT_LIMIT,
  pollInterval = DEFAULT_POLL_INTERVAL,
}: UseDealActivitiesOptions): UseDealActivitiesReturn => {
  const client = useApolloClient();
  const [activities, setActivities] = useState<DealActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const isMountedRef = useRef(true);

  const fetchActivities = useCallback(
    async (beforeId?: string): Promise<DealActivity[]> => {
      if (!dealId) return [];

      try {
        const { data } = await client.query({
          query: getDealActivities,
          variables: {
            dealId,
            limit,
            beforeId: beforeId || null,
          },
          fetchPolicy: 'network-only',
        });

        const rawActivities = data?.getDealById?.dealActivities || [];

        // Map backend camelCase fields to the snake_case format the UI expects
        return rawActivities.map((a: any) => ({
          id: a.id,
          verb: a.verb,
          time: a.time,
          description: a.description,
          actor_data: a.actorData,
          object_data: a.objectData,
          additional_message: a.additionalMessage,
        }));
      } catch (err) {
        console.error('Error fetching deal activities:', err);
        throw err;
      }
    },
    [client, dealId, limit]
  );

  const refetch = useCallback(async () => {
    if (!dealId || !isMountedRef.current) return;

    try {
      const newActivities = await fetchActivities();
      if (isMountedRef.current) {
        setActivities(newActivities);
        setHasMore(newActivities.length === limit);
        setError(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err as Error);
      }
    }
  }, [fetchActivities, dealId, limit]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || activities.length === 0) return;

    setLoading(true);
    try {
      const lastId = activities[activities.length - 1].id;
      const moreActivities = await fetchActivities(lastId);
      if (isMountedRef.current) {
        const combined = [...activities, ...moreActivities];
        setActivities(combined);
        setHasMore(moreActivities.length === limit);
      }
    } catch (err) {
      console.error('Error loading more deal activities:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [activities, hasMore, loading, fetchActivities, limit]);

  // Initial load
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!dealId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const initialActivities = await fetchActivities();
        if (!cancelled && isMountedRef.current) {
          setActivities(initialActivities);
          setHasMore(initialActivities.length === limit);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled && isMountedRef.current) {
          setError(err as Error);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [dealId, fetchActivities, limit]);

  // Polling
  useEffect(() => {
    if (!dealId || !pollInterval) return;

    const interval = setInterval(() => {
      if (isMountedRef.current) {
        refetch().catch((err) => {
          console.warn('Deal activities polling error (non-critical):', err);
        });
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [dealId, pollInterval, refetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    activities,
    loading,
    error,
    refetch,
    loadMore,
    hasMore,
    totalLoaded: activities.length,
  };
};
